import React from 'react';
import { graphql, useFragment, useMutation } from 'react-relay';
import { ButtonItem } from '@atlaskit/menu';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersions_jiraIssue$key } from '@atlassian/jira-relay/src/__generated__/moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersions_jiraIssue.graphql';
import type { moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersions_jiraVersion$key } from '@atlassian/jira-relay/src/__generated__/moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersions_jiraVersion.graphql';
import type { moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersionsMutation } from '@atlassian/jira-relay/src/__generated__/moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersionsMutation.graphql';
import { isDevopsFeatureDisabledInFedRamp } from '@atlassian/jira-software-devops-fedramp-utils';
import { useVersionIssuesGraphQLVariables } from '@atlassian/jira-software-releases-version-detail-issue-list-query-param-controller/src/controllers/graphql-variables/index.tsx';
import {
	useMoveIssuesBetweenVersionsExperienceStart,
	useMoveIssuesBetweenVersionsExperienceFail,
	useMoveIssuesBetweenVersionsExperienceSuccess,
} from '../../../services/move-issues-experience-tracker';
import messages from './messages';
import { getErrorMessage } from './utils';

type Props = {
	jiraVersion: moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersions_jiraVersion$key;
	jiraIssue: moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersions_jiraIssue$key;
	originalJiraVersion: string;
};

export const MoveIssueToOtherVersion = ({ jiraVersion, jiraIssue, originalJiraVersion }: Props) => {
	const { showFlag } = useFlagsService();
	const { formatMessage } = useIntl();

	const issue = useFragment(
		graphql`
			fragment moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersions_jiraIssue on JiraIssue {
				id @required(action: THROW)
				key @required(action: THROW)
			}
		`,
		jiraIssue,
	);
	const version = useFragment(
		graphql`
			fragment moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersions_jiraVersion on JiraVersion {
				id @required(action: THROW)
				name @required(action: THROW)
			}
		`,
		jiraVersion,
	);

	const [moveIssuesToFixVersion] =
		useMutation<moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersionsMutation>(graphql`
			mutation moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersionsMutation(
				$input: JiraMoveIssuesToFixVersionInput!
				$sortBy: JiraVersionIssuesSortInput
				$filters: JiraVersionIssuesFiltersInput
				$isDevopsFeatureDisabledInFedRamp: Boolean!
				$isIssueSummarisedDeploymentsMigratedToAgs: Boolean!
				$shouldIncludeDevOpsFieldsViaAgs: Boolean!
			) {
				jira {
					moveIssuesToFixVersion(input: $input) {
						success
						errors {
							message
						}
						issuesWithMissingResolvePermission
						issuesWithMissingEditPermission
						originalVersion {
							...addRelatedWorkPopup_softwareReleasesAddRelatedWorkToVersion_jiraVersion
							...issueTotalCount_softwareReleasesVersionDetailIssueList
							...ui_softwareReleasesVersionDetail_VersionDetailInner_jiraVersion
							...ui_softwareReleasesVersionDetailIssueList_IssuesRefetchable_jiraVersion
						}
					}
				}
			}
		`);
	const { filters, sortBy } = useVersionIssuesGraphQLVariables();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'button',
	});

	const experienceStart = useMoveIssuesBetweenVersionsExperienceStart();
	const experienceFail = useMoveIssuesBetweenVersionsExperienceFail();
	const experienceSuccess = useMoveIssuesBetweenVersionsExperienceSuccess();

	const onError = (err: Error) => {
		showFlag({
			type: 'error',
			title: formatMessage(messages.errorFlagDescription, {
				issueKey: issue.key,
				versionName: version.name,
			}),
		});
		experienceFail('graphql error', err);
		fireErrorAnalytics({
			error: err,
			meta: {
				id: 'jira.releases.remove-issue-from-version.mutation',
				packageName: 'jiraSoftwareReleasesMoveIssuesBetweenVersions',
				teamName: 'fusion-solaris',
			},
			sendToPrivacyUnsafeSplunk: true,
		});
	};

	const onErrorWithPermissionValidationEnabled = (
		issueKey: string,
		issuesWithMissingResolvePermission: readonly string[] | null | undefined,
		issuesWithMissingEditPermission: readonly string[] | null | undefined,
	) => {
		const error = getErrorMessage(
			issueKey,
			issuesWithMissingResolvePermission,
			issuesWithMissingEditPermission,
			formatMessage,
		);
		showFlag({
			type: 'error',
			title: formatMessage(messages.errorFlagTitle),
			description: error,
		});
		experienceFail('graphql error', new Error(error));
		fireErrorAnalytics({
			error: new Error(error),
			meta: {
				id: 'jira.releases.remove-issue-from-version.mutation',
				packageName: 'jiraSoftwareReleasesMoveIssuesBetweenVersions',
				teamName: 'fusion-solaris',
			},
			sendToPrivacyUnsafeSplunk: true,
		});
	};

	const onUpdateVersion = () => {
		moveIssuesToFixVersion({
			variables: {
				input: {
					issueIds: [issue.id],
					originalVersionId: originalJiraVersion,
					targetVersionId: version.id,
				},
				isDevopsFeatureDisabledInFedRamp: isDevopsFeatureDisabledInFedRamp(),
				isIssueSummarisedDeploymentsMigratedToAgs: fg(
					'solaris_migrate_summurized_deployments_to_ags_re',
				),
				shouldIncludeDevOpsFieldsViaAgs:
					!isDevopsFeatureDisabledInFedRamp() &&
					fg('solaris_migrate_summurized_deployments_to_ags_re'),
				filters,
				sortBy,
			},
			onCompleted: (data) => {
				if (data?.jira?.moveIssuesToFixVersion?.success !== true) {
					onErrorWithPermissionValidationEnabled(
						issue.key,
						data?.jira?.moveIssuesToFixVersion?.issuesWithMissingResolvePermission,
						data?.jira?.moveIssuesToFixVersion?.issuesWithMissingEditPermission,
					);
				} else {
					const flagText = formatMessage(messages.successFlagDescription, {
						issueKey: issue.key,
						versionName: version.name,
					});
					showFlag({ type: 'success', title: flagText });
					experienceSuccess();
				}
			},
			onError,
		});
	};

	return (
		<ButtonItem
			onClick={() => {
				fireUIAnalytics(analyticsEvent, 'moveIssueToVersion');
				experienceStart();
				onUpdateVersion();
			}}
		>
			{version.name}
		</ButtonItem>
	);
};
